import React, { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight, LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import type { DashboardCounters } from '../../hooks/useDashboardCounters';
import { visibleAccountNav, currentSectionLabel } from './accountNav';
import { useContatto } from '../../hooks/useContatto';

/**
 * Navigazione mobile dell'Area Cliente: barra di sezione sticky + drawer.
 *
 * Sostituisce la sidebar impilata, che sotto `lg` occupava il 91% della
 * prima schermata (13 voci, ~700px, `<h1>` a y = 765px) e costringeva a
 * risalire l'intero documento per cambiare sezione.
 *
 * Costruito su `@radix-ui/react-dialog` — che fornisce focus trap, scroll
 * lock, chiusura con Escape, `role="dialog"` con `aria-labelledby` e
 * ripristino del focus al trigger — invece che sul wrapper `ui/sheet.jsx`:
 * quello è un `.jsx` non tipizzato mai importato da nessuno (quindi con la
 * risoluzione dell'alias `@/` non esercitata) e porta con sé un pulsante
 * di chiusura fisso che qui andrebbe comunque sovrascritto. In `src/` non
 * esiste alcuna utility di scroll-lock o focus-trap da riusare.
 *
 * Nota: Radix **non** emette `aria-modal`; rende inerte il resto della
 * pagina marcandolo `aria-hidden`, che ai fini assistivi è equivalente.
 */
/** I contatori arrivano dal layout: chiamare l'endpoint anche qui
    raddoppierebbe la richiesta a ogni pagina dell'area. */
export function AccountDrawer({ counters }: { counters: DashboardCounters }): React.ReactElement {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const contatto = useContatto();
  const groups = visibleAccountNav(user?.ruolo, contatto);
  const section = currentSectionLabel(location.pathname);
  const badgeTotal = Object.values(counters).reduce((a, b) => a + b, 0);

  /* Radix non chiude al cambio di route: senza questo il pannello resta
     aperto sopra la pagina nuova. */
  useEffect(() => { setOpen(false); }, [location.pathname]);

  /* Swipe verso sinistra per chiudere — gesto atteso su un pannello
     off-canvas da sinistra, e Radix non lo copre. */
  const touchX = useRef<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchX.current !== null && touchX.current - e.changedTouches[0].clientX > 60) {
      setOpen(false);
    }
    touchX.current = null;
  };

  return (
    /* Nessun wrapper attorno alla barra: un div contenitore sarebbe alto
       quanto la barra stessa (48px), e `position: sticky` si incolla
       *dentro il box del genitore* — con 48px di margine di manovra la
       barra scorreva via insieme alla pagina. Il fragment la lascia figlia
       diretta del contenitore di pagina, che è alto quanto il documento. */
    <>
      {/* ── Barra di sezione sticky ──
          Fa tre cose insieme: apre il drawer, dice dove sei (nell'area
          riservata non c'è breadcrumb) e mostra il totale dei badge.

          `top` = altezza dell'header (`h-16` = 4rem, la stessa misura in
          rem a prescindere dal root a 17px) più l'eventuale banner di
          impersonazione, di cui l'header stesso tiene conto con la
          medesima variabile. Così la barra si incolla **sotto** l'header
          invece di scivargli dietro: z-40 contro z-50 dell'header. */}
      <div
        className="no-print lg:hidden sticky z-40 border-b bg-white"
        style={{ top: 'calc(var(--impersonation-banner-h, 0px) + 4rem)' }}
        data-testid="account-section-bar"
      >
        <div className="max-w-7xl mx-auto flex items-center gap-2 px-4 sm:px-6">
        <Dialog.Root open={open} onOpenChange={setOpen}>
          <Dialog.Trigger asChild>
            <button
              type="button"
              className="flex min-h-[48px] flex-1 items-center gap-2 text-left text-[15px]"
              data-testid="account-drawer-toggle"
              aria-label="Apri il menu dell'Area Cliente"
            >
              <Menu className="h-5 w-5 flex-none text-[hsl(210,64%,29%)]" aria-hidden="true" />
              <span className="text-muted-foreground">Area Cliente</span>
              {section && (
                <>
                  <ChevronRight className="h-4 w-4 flex-none text-muted-foreground" aria-hidden="true" />
                  <span className="truncate font-semibold">{section}</span>
                </>
              )}
            </button>
          </Dialog.Trigger>

          <Dialog.Portal>
            <Dialog.Overlay
              className="fixed inset-0 z-[95] bg-[rgb(15_23_42/0.55)] data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 motion-reduce:animate-none"
              data-testid="account-drawer-overlay"
            />
            <Dialog.Content
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
              className="fixed inset-y-0 left-0 z-[95] flex w-[min(86vw,340px)] flex-col bg-white shadow-[4px_0_24px_rgb(0_0_0/0.18)] [overscroll-behavior:contain] duration-200 data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left motion-reduce:animate-none motion-reduce:duration-0"
              data-testid="account-drawer"
            >
              <div className="flex items-center justify-between border-b px-4 py-3">
                <Dialog.Title className="text-base font-semibold">Area Cliente</Dialog.Title>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    className="-mr-2 flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground hover:bg-[hsl(210,40%,96%)]"
                    aria-label="Chiudi menu"
                    data-testid="account-drawer-close"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </Dialog.Close>
              </div>
              {/* Radix richiede una descrizione o l'esplicita assenza. */}
              <Dialog.Description className="sr-only">
                Navigazione fra le sezioni dell&apos;Area Cliente
              </Dialog.Description>

              <nav
                className="flex-1 overflow-y-auto [-webkit-overflow-scrolling:touch] py-1"
                aria-label="Navigazione area clienti"
              >
                {groups.map((group, gi) => (
                  <div key={group.label ?? `g${gi}`}>
                    {group.label && (
                      <p className="px-4 pb-1 pt-4 text-xs font-semibold text-[#4B5563]">
                        {group.label}
                      </p>
                    )}
                    <ul>
                      {group.items.map(item => {
                        const Icon = item.icon;
                        const count = item.badge ? counters[item.badge as keyof DashboardCounters] : 0;
                        return (
                          <li key={item.to}>
                            <NavLink
                              to={item.to}
                              end={item.exact}
                              data-testid={item.testId}
                              className={({ isActive }) =>
                                `flex min-h-[48px] items-center gap-3 border-l-[3px] px-4 text-[15px] transition-colors ${
                                  isActive
                                    ? 'border-l-[hsl(210,64%,29%)] bg-[#EFF4FB] font-semibold text-[hsl(210,64%,29%)]'
                                    : 'border-l-transparent text-[#14181F] hover:bg-[hsl(210,40%,96%)]'
                                }`
                              }
                            >
                              <Icon className="h-5 w-5 flex-none" aria-hidden="true" />
                              <span className="flex-1 truncate">{item.label}</span>
                              {count > 0 && (
                                <span
                                  data-testid={`${item.testId}-badge`}
                                  className="inline-flex h-[20px] min-w-[20px] items-center justify-center rounded-full bg-rose-600 px-1 text-[11px] font-bold text-white"
                                >
                                  {count > 99 ? '99+' : count}
                                </span>
                              )}
                            </NavLink>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </nav>

              {/* Identità + logout in un blocco separato: nella sidebar il
                  "Log out" stava subito sotto "Preferenze" con la stessa
                  forma delle voci di navigazione, a rischio di tocco
                  accidentale scorrendo la lista. */}
              <div
                className="border-t px-4 py-3"
                style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
              >
                <p className="truncate text-sm font-semibold" title={user?.nome ?? ''}>
                  {user?.nome ?? user?.email ?? '—'}
                </p>
                {user?.idCliente && (
                  <p className="font-mono text-[13px] text-muted-foreground">cod. {user.idCliente}</p>
                )}
                <button
                  type="button"
                  onClick={logout}
                  data-testid="nav-logout"
                  className="mt-2 flex min-h-[44px] w-full items-center gap-2 rounded-lg text-sm text-[#B42318] hover:bg-rose-50"
                >
                  <LogOut className="h-4 w-4 flex-none" aria-hidden="true" />
                  Esci
                </button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        {badgeTotal > 0 && (
          <span
            data-testid="account-section-bar-badge"
            aria-label={`${badgeTotal} novità`}
            title={`${badgeTotal} novità: ordini aggiornati e messaggi da leggere`}
            className="inline-flex h-[20px] min-w-[20px] flex-none items-center justify-center rounded-full bg-rose-600 px-1 text-[11px] font-bold text-white"
          >
            {badgeTotal > 99 ? '99+' : badgeTotal}
          </span>
        )}
        </div>
      </div>
    </>
  );
}
